import type { SyncStep } from '../../models/development/sync'
import { formatNumber } from '../../utils/format-number'

import { BACKGROUND_JOB } from './background-job'
import { COMMON } from './common'
import { NAV } from './nav'
import { TERMS } from './terms'

/** The three phases either side of the reconcile, which walks the tables. */
const SYNC_PHASES = {
  snapshot: 'Snapshot wird geholt',
  media: 'Bilder werden kopiert',
  picks: 'Auswahlen werden bereinigt',
} satisfies Record<Exclude<SyncStep, { kind: 'table' }>['kind'], string>

/**
 * The Development area: what a developer does to an environment that is not
 * prod.
 *
 * Its vocabulary is the technical one deliberately — Sync, Snapshot, Origin,
 * Environment — because the thing it operates on is the deployment, not the
 * business. The German is for what a person does with it: the buttons, the
 * confirmations, what a figure means.
 */
export const DEVELOPMENT = {
  title: 'Development',
  sync: {
    title: NAV.sync,
    hint: `Holt Katalog, ${TERMS.configurator} und alles, was dazugehört, aus prod in diese Umgebung. Projekte, Benutzer und was hier passiert ist bleiben unberührt.`,
    /** The one sentence the screen leads with where it cannot be a target. */
    notATarget:
      'Diese Umgebung ist die Quelle und kann nicht ihr eigenes Ziel sein.',
    notATargetHint:
      'Der Sync läuft nur in eine Richtung: aus prod heraus in jede andere Umgebung.',
    origin: TERMS.source,
    lastSync: 'Letzter Sync',
    never: 'Noch keiner',
    start: 'Jetzt synchronisieren',
    running: 'Sync läuft …',
    failed: 'Sync fehlgeschlagen',
    step: (step: SyncStep) =>
      step.kind === 'table' ? `Tabelle ${step.table}` : SYNC_PHASES[step.kind],
    /** The login that fetches the snapshot. */
    credentials: 'Zugang zur Quelle',
    credentialsHint:
      'Deine eigenen prod-Zugangsdaten. Sie werden nur für diesen Sync benutzt und nirgends gespeichert; prod protokolliert, wer gezogen hat.',
    confirmTitle: 'Katalog aus prod übernehmen',
    confirmMessage: `Der Katalog, der ${TERMS.configurator} und die Produkteigenschaften dieser Umgebung werden auf den Stand der Quelle gebracht. Projekte, Benutzer und Medien, die hier entstanden sind, bleiben.`,
    confirmLabel: 'Synchronisieren',

    /** What the sync did, once it is through. */
    summary: {
      title: 'Ergebnis',
      table: 'Tabelle',
      inserted: 'Neu',
      updated: 'Geändert',
      deleted: 'Entfernt',
      unchanged: 'Unverändert',
      kept: 'Behalten',
      keptHint:
        'Die Quelle hat diese Zeilen nicht mehr, hier hängt aber noch etwas daran — zum Beispiel ein Element-Typ, in dem ein lokales Objekt vermessen ist.',
      nothingChanged: 'Alles war schon auf dem Stand der Quelle.',
      images: COMMON.images,
      imagesResult: (media: {
        copied: number
        unchanged: number
        missing: number
      }) =>
        [
          `${formatNumber(media.copied)} kopiert`,
          `${formatNumber(media.unchanged)} schon vorhanden`,
          ...(media.missing > 0
            ? [`${formatNumber(media.missing)} in der Quelle nicht gefunden`]
            : []),
        ].join(', '),
      unknownColumns: 'Übersprungene Spalten',
      unknownColumnsHint:
        'Die Quelle kennt diese Spalten, dieses Schema nicht. Sie wurden weggelassen — die Quelle ist eine Migration weiter.',

      /** What the sync settled on the way, so nothing is left to do after. */
      pruned: 'Mit aufgeräumt',
      prunedHint:
        'Was an lokalen Projekten auf Katalog-Einträge zeigte, die es in der Quelle nicht mehr gibt. Im selben Durchlauf bereinigt.',
      prunedSelections: (count: number) =>
        count === 1
          ? '1 Konfiguration bereinigt'
          : `${formatNumber(count)} Konfigurationen bereinigt`,
      prunedPositions: (count: number) =>
        count === 1
          ? '1 Position entfernt'
          : `${formatNumber(count)} Positionen entfernt`,
      prunedNothing: 'Nichts zu bereinigen',
    },

    /** The one line the change history carries for a whole sync. */
    historyLabel: (origin: string, changed: number) =>
      changed === 0
        ? `Aus ${origin} geholt, nichts geändert`
        : `Aus ${origin} geholt, ${changed} Zeilen geändert`,

    /**
     * The last four are met inside the worker. They reach the screen as the
     * job's failure reason, which `failJob` resolves through the same table a
     * request's refusal goes through.
     */
    errors: {
      alreadyRunning: (live: number) =>
        BACKGROUND_JOB.alreadyRunning(live, 'ein Sync'),
      notATarget: 'Diese Umgebung ist die Quelle eines Syncs, nie sein Ziel.',
      originRefused:
        'Die Quelle hat den Zugang abgelehnt. Stimmen E-Mail und Passwort, und ist das Konto dort ROOT?',
      originUnreachable: (origin: string) =>
        `${origin} ist nicht erreichbar. Läuft die Quelle, und kommt diese Umgebung ins Netz?`,
      snapshotRefused:
        'Die Quelle hat den Snapshot verweigert. Das Konto dort braucht ROOT.',
      behindSchema: (detail: string) =>
        `Die Quelle ist älter als das Schema dieser Umgebung: ${detail} fehlt im Snapshot.`,
      noMediaBucket:
        'Die Quelle nennt keinen Bucket für ihre Bilder. Ohne den käme der Katalog ohne Bilder an.',
    },
  },
} as const
